import { loading, search } from "@simplybusiness/icons";
import { Combobox, Icon, type ComboboxOptions } from "@simplybusiness/mobius";
import { forwardRef, useCallback, useRef } from "react";
import { TradeSelectorOption } from "./TradeSelectorOption";
import { DEFAULT_TRADES_ENDPOINT, LABELS, PLACEHOLDERS } from "./constants";
import { groupResults } from "./groupResults";
import { useFetchTrades } from "../../hooks/useFetchTrades";
import type { TradeSelectorProps, TradeOption } from "./types";

export const TradeSelector = forwardRef<HTMLInputElement, TradeSelectorProps>(
  (
    {
      onSelectTrade,
      site,
      vertical,
      searchScope,
      tradesEndpoint = DEFAULT_TRADES_ENDPOINT,
      placeholder,
      variant = "default",
      primaryDetail,
      ...otherProps
    },
    ref,
  ) => {
    // Store search metadata for event tracking
    const searchIdRef = useRef<string | undefined>(undefined);
    const flatOptionsRef = useRef<TradeOption[]>([]);

    // Initialize the hook for fetching trades
    const { mutateAsync, isPending } = useFetchTrades<
      ComboboxOptions<TradeOption>
    >({
      site,
      vertical,
      searchScope,
      tradesEndpoint,
      variant,
      primaryDetail,
      transformResults: groupResults,
    });

    // Create async function for Combobox asyncOptions
    const fetchTrades = useCallback(
      async (query: string) => {
        const response = await mutateAsync(query);
        // Store searchId and flatten options for index lookup
        searchIdRef.current = response.searchId;
        flatOptionsRef.current = response.results.flatMap(group =>
          "options" in group ? group.options : [group],
        );
        return response.results;
      },
      [mutateAsync],
    );

    const searchPlaceholder = placeholder || PLACEHOLDERS[variant];

    // Show loading spinner as prefix while fetching data
    const prefixIcon = isPending ? (
      <Icon icon={loading} spin />
    ) : (
      <Icon icon={search} />
    );

    // Handle selection with metadata for analytics
    const handleSelected = useCallback(
      (option: TradeOption) => {
        const index = flatOptionsRef.current.findIndex(
          o => o.value === option.value,
        );
        onSelectTrade?.(option, {
          index: index >= 0 ? index : undefined,
          searchId: searchIdRef.current,
          variant,
        });
      },
      [onSelectTrade, variant],
    );

    return (
      <Combobox
        ref={ref}
        label={LABELS[variant]}
        placeholder={searchPlaceholder}
        asyncOptions={fetchTrades}
        onSelected={handleSelected}
        optionComponent={TradeSelectorOption}
        data-variant={variant}
        icon={prefixIcon}
        {...otherProps}
      />
    );
  },
);

TradeSelector.displayName = "TradeSelector";
